import { useMemo, useCallback } from 'react';
import {
  useAppSelector,
  useComparisonPeriod,
  ComparisonPeriod,
  useMetricFormatter,
  useWeekRange,
  WeekRange
} from 'src/utils/Hooks';
import { calculatePercentChange, safeDivide } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import {
  getFirstWeekIdOfYear,
  getNextYearWeekId,
  getPreviousWeekId,
  getPreviousYearWeekId,
  timestampToWeekId,
  weekIdToTimestamp,
  yearPartOfWeekId
} from 'src/utils/dateUtils';
import { ValueOf } from 'sl-api-connector';
import { useDorisApplyEntityFilters } from 'src/utils/Hooks/useDorisApplyEntityFilters';
import useDorisMarketShareHooks from './useDorisMarketShareHooks';
import { useDorisData, UseDorisDataProps } from 'src/utils/Hooks/useDorisData';
import { useRetailerId } from 'src/utils/Hooks/reduxSelectors';

// Define the response type for our endpoint
export interface TrafficSummaryResponse {
  metricType: string;
  [key: string]: any; // Allow any field to be accessed dynamically
  totalClicks?: number;
  organicClicks?: number;
  adClicks?: number;
  otherClicks?: number;
  // New API format with records
  records?: Array<{
    weekId: number;
    value: number;
  }>;
  // no need to have this it is debug, but it is useful for debugging
  debug?: {
    query: string;
    queryExecutionTime: number;
    queryContextCreationTime: number;
    isCacheHit: boolean;
    cacheResponseTime: number;
    topicGroupAnnotationTime: number;
  };
}

// Define the market share response type
export interface MarketShareResponse extends TrafficSummaryResponse {
  marketShare?: number;
  marketTotal?: number;
}

// Define the parameter type for our hook
export interface UseDorisMetricByWeekIdArgs {
  /**
   *  not used for fetching, this is just reactQueryEky
   * */
  queryKey: string;
  metricType: string;
  categoryIds?: number[];
  subcategoryIds?: number[];
  methodName?: string;
  brandIds?: number[];
  segmentIds?: string[];
  baseParamsBuilder?: (params: Record<string, unknown>) => Record<string, unknown>;
  /**
   * True if we want to shift the prior year comparison
   * data up to match the current year
   */
  isComparisonDataCustom?: boolean;
  /**
   * Will use most recent week's data instead of full time period
   */
  useLatestWeek?: boolean;
  /**
   * METRIC TYPE (PERCENT, VOLUME, etc), backend calls the doris name As metricType too ,
   */
  metricTypeDisplay?: ValueOf<typeof METRICTYPE>;

  // Optional min and max retail price filters
  minRetailPrice?: number;
  maxRetailPrice?: number;
  keywords?: string[];

  resultCount?: number;

  /**
   * When true, only fetches the primary data without comparison
   */
  skipComparison?: boolean;

  /**
   * Function to transform primary data into parameters for comparison query
   * Used in the third scenario where comparison data depends on primary data
   */
  getComparisonParamsFromPrimary?: (primaryData: TrafficSummaryResponse) => {
    categoryIds?: number[];
    subcategoryIds?: number[];
    brandIds?: number[];
    segmentIds?: string[];
  };

  /**
   * Field name to use for the main metric value
   * If not provided, it will be automatically selected based on metricType
   * @example 'totalClicks', 'organicImpressions', etc.
   */
  metricField?: string;

  /**
   * Multiple fields to include in the response for additional metrics
   * @example ['organicClicks', 'adClicks', 'otherClicks']
   */
  additionalMetricFields?: string[];

  /**
   * When true, calculates and displays market share metrics
   */
  showMarketShare?: boolean;

  /**
   * Optional field to use as the market total for calculating market share
   * If not provided, will use the same field as metricField at market level
   */
  marketShareField?: string;

  /**
   * Function to build the market share request
   */
  marketShareRequestBuilder?: (params: any, isComparison: boolean) => any;

  /**
   * Function to build the market share numerator request
   */
  marketShareNumeratorRequestBuilder?: (
    params: Omit<UseDorisDataProps, 'metricType'> | 'branding'
  ) => UseDorisDataProps;
}

/**
 * Takes raw response data and formats it into a time series for charts
 */
const getSplineDataFromResponse = (
  response: TrafficSummaryResponse | MarketShareResponse,
  weekIds: number[],
  metricField: string,
  isMarketShare: boolean = false
): number[][] => {
  if (!response) {
    return [];
  }

  // Check if the response has records array (new API format)
  if (response.records && Array.isArray(response.records)) {
    // Map each record to a data point [timestamp, value]
    return response.records
      .sort((a, b) => a.weekId - b.weekId)
      .map((record) => [weekIdToTimestamp(record.weekId), isMarketShare ? record.value || 0 : record.value || 0]);
  }

  // Fallback to old format (direct property access)
  const metricValue = response[metricField] || response[response.metricType] || 0;

  // Distribute the total value evenly across the weeks
  const valuePerWeek = metricValue / weekIds.length;

  return weekIds.map((weekId) => [
    weekIdToTimestamp(weekId),
    // If it's market share data, multiply by 100 to display as percentage
    isMarketShare ? valuePerWeek : valuePerWeek
  ]);
};

/**
 * Takes raw response data and calculates market share values
 */
const calculateMarketShare = (
  clientData: TrafficSummaryResponse | undefined,
  marketData: TrafficSummaryResponse | undefined,
  metricField: string,
  marketShareField?: string,
  shouldFilterByCorrespondingWeeks: boolean = false,
  weekIdsInRange: number[] = [],
  isComparison: boolean = false
): {
  marketShareValue: number;
  clientValue: number;
  marketValue: number;
  weeklyMarketShare?: Array<{ weekId: number; value: number }>;
} => {
  if (!clientData || !marketData) {
    return {
      marketShareValue: 0,
      clientValue: 0,
      marketValue: 0
    };
  }

  // Check if we have records (week-by-week data)
  if (clientData.records && marketData.records) {
    // Total values for aggregated metrics
    let totalClientValue = 0;
    let totalMarketValue = 0;
    let ytdClientValue = 0;
    let ytdMarketValue = 0;

    // Create a map for easier lookup of client data by weekId
    const clientDataByWeekId = new Map(clientData.records.map((record) => [record.weekId, record.value || 0]));

    const weeksToUse = weekIdsInRange.map((weekId) => getPreviousYearWeekId(weekId));

    const weeklyMarketShare = marketData.records
      .sort((a, b) => a.weekId - b.weekId)
      .map((record) => {
        const { weekId, value: marketValue = 0 } = record;
        const clientValue = clientDataByWeekId.get(weekId) || 0;
        const marketShareValue = safeDivide(clientValue, marketValue);

        totalClientValue += clientValue;
        totalMarketValue += marketValue;
        if (weeksToUse.includes(weekId)) {
          ytdClientValue += clientValue;
          ytdMarketValue += marketValue;
        }

        return {
          weekId,
          value: marketShareValue
        };
      });

    // Calculate overall market share
    const overallMarketShare =
      shouldFilterByCorrespondingWeeks && isComparison
        ? safeDivide(ytdClientValue, ytdMarketValue)
        : safeDivide(totalClientValue, totalMarketValue);

    return {
      marketShareValue: overallMarketShare,
      clientValue: totalClientValue,
      marketValue: totalMarketValue,
      weeklyMarketShare
    };
  }

  // Fallback to old format (direct property access)
  const fieldToUse = metricField || clientData.metricType || '';
  const marketFieldToUse = marketShareField || fieldToUse;
  const clientValue = clientData[fieldToUse] || 0;
  const marketValue = marketData[marketFieldToUse] || 0;
  const marketShareValue = safeDivide(clientValue, marketValue);

  return {
    marketShareValue,
    clientValue,
    marketValue
  };
};

/**
 * A hook to fetch and format traffic metrics data from the Doris API
 */
export const useDorisMetricByWeekId = ({
  queryKey,
  metricType,
  resultCount,
  showMarketShare = false,
  baseParamsBuilder,
  metricTypeDisplay,
  useLatestWeek = false,
  isComparisonDataCustom,
  skipComparison = false,
  getComparisonParamsFromPrimary,
  metricField,
  methodName = 'keymetrics',
  additionalMetricFields = [],
  marketShareField,
  marketShareRequestBuilder: customMarketShareRequestBuilder,
  marketShareNumeratorRequestBuilder: customMarketShareNumeratorRequestBuilder
}: UseDorisMetricByWeekIdArgs) => {
  const formatMetric = useMetricFormatter();
  const comparisonPeriod = useComparisonPeriod();
  const weekRange = useWeekRange();
  const applyEntityFilters = useDorisApplyEntityFilters();
  const retailerId = useRetailerId();
  // Use the market share hooks if showMarketShare is true
  const {
    requestBuilder: defaultMarketShareNumeratorRequestBuilder,
    marketShareRequestBuilder: defaultMarketShareRequestBuilder
  } = useDorisMarketShareHooks();

  // Use custom marketShareRequestBuilder if provided, otherwise use the default
  const marketShareNumeratorRequestBuilder =
    customMarketShareNumeratorRequestBuilder || defaultMarketShareNumeratorRequestBuilder;

  const marketShareRequestBuilder = customMarketShareRequestBuilder || defaultMarketShareRequestBuilder;

  // Determine which metric field to use, either from props or default mapping
  const metricFieldToUse = useMemo(() => {
    return metricField || metricType;
  }, [metricField, metricType]);

  // Special case, when comparing last week with prior year we want
  // to show the graph for YTD but the legend should just be last week
  const isLastWeekWithPriorYear = useMemo(() => {
    return weekRange === WeekRange.LAST_WEEK && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR;
  }, [comparisonPeriod, weekRange]);

  const defaultStartWeekId = useAppSelector((state) => state.mainTimePeriod.startWeek);
  const endWeekId = useAppSelector((state) => state.mainTimePeriod.endWeek);

  const startWeekId = useMemo(() => {
    if (useLatestWeek) {
      return endWeekId;
    }

    return isLastWeekWithPriorYear ? getFirstWeekIdOfYear(yearPartOfWeekId(endWeekId)) : defaultStartWeekId;
  }, [defaultStartWeekId, endWeekId, isLastWeekWithPriorYear, useLatestWeek]);

  const defaultComparisonStartWeekId = useAppSelector((state) => state.comparisonTimePeriod.startWeek);
  const defaultComparisonEndWeekId = useAppSelector((state) => state.comparisonTimePeriod.endWeek);

  const comparisonStartWeekId = useMemo(
    () => (isLastWeekWithPriorYear ? getPreviousYearWeekId(startWeekId) : defaultComparisonStartWeekId),
    [defaultComparisonStartWeekId, isLastWeekWithPriorYear, startWeekId]
  );

  const comparisonEndWeekId = useMemo(
    () =>
      isLastWeekWithPriorYear || (weekRange === WeekRange.YTD && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR)
        ? getPreviousWeekId(startWeekId)
        : defaultComparisonEndWeekId,
    [comparisonPeriod, defaultComparisonEndWeekId, isLastWeekWithPriorYear, startWeekId, weekRange]
  );

  // Generate all weekIds in range for our data points
  const weekIdsInRange = useMemo(() => {
    const result: number[] = [];
    let current = startWeekId;

    while (current <= endWeekId) {
      result.push(current);
      // Logic to increment week id correctly
      if (current % 100 === 52) {
        // End of year, move to next year
        current = (Math.floor(current / 100) + 1) * 100 + 1;
      } else {
        // Increment week within same year
        current += 1;
      }
    }

    return result;
  }, [startWeekId, endWeekId]);

  // Generate all weekIds in comparison range
  const comparisonWeekIdsInRange = useMemo(() => {
    if (skipComparison) {
      return [];
    }

    const result: number[] = [];
    let current = comparisonStartWeekId;

    while (current <= comparisonEndWeekId) {
      result.push(current);
      // Logic to increment week id correctly
      if (current % 100 === 52) {
        // End of year, move to next year
        current = (Math.floor(current / 100) + 1) * 100 + 1;
      } else {
        // Increment week within same year
        current += 1;
      }
    }

    return result;
  }, [comparisonStartWeekId, comparisonEndWeekId, skipComparison]);

  // Apply entity filters to the base parameters
  const baseParams = useMemo(() => {
    const initialParams = {
      metricType,
      retailerId: +retailerId
    };

    const paramsToUse = baseParamsBuilder ? baseParamsBuilder(initialParams) : initialParams;
    // Apply entity and active filters
    return applyEntityFilters(paramsToUse, showMarketShare);
  }, [metricType, retailerId, applyEntityFilters, showMarketShare, baseParamsBuilder]);

  const { data: primaryData, isLoading: isPrimaryLoading } = useDorisData<TrafficSummaryResponse>({
    methodName,
    metricType,
    queryKey: `${queryKey}_${metricType}_${startWeekId}_${endWeekId}`,
    resultCount: resultCount || 1200,
    ...baseParams,
    customTimePeriods: {
      startWeekId,
      endWeekId
    },
    additionalParams: showMarketShare ? marketShareNumeratorRequestBuilder(baseParams) : {},
    skipComparison: true, // We'll handle comparison data separately
    queryOptions: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  });

  // Market data query for the primary period
  const { data: marketPrimaryData, isLoading: isMarketPrimaryLoading } = useDorisData<TrafficSummaryResponse>({
    methodName,
    metricType,
    resultCount: resultCount || 1200,
    ...baseParams,
    queryKey: `market_${queryKey}_${metricType}_${startWeekId}_${endWeekId}`,
    customTimePeriods: {
      startWeekId,
      endWeekId
    },
    skipComparison: true,
    additionalParams: showMarketShare
      ? marketShareRequestBuilder(
          {
            ...baseParams,
            startWeekId,
            endWeekId
          },
          false
        ) || {}
      : {},
    enabled: showMarketShare,
    queryOptions: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  });

  // Prepare parameters for comparison query based on primary data if needed
  const comparisonParams = useMemo(() => {
    if (skipComparison) {
      return null;
    }

    // If we need to transform the primary data to get comparison params
    if (getComparisonParamsFromPrimary && primaryData) {
      const dynamicParams = getComparisonParamsFromPrimary(primaryData);
      const comparisonBaseParams = {
        ...baseParams,
        startWeekId: comparisonStartWeekId,
        endWeekId: comparisonEndWeekId,
        categoryIds: dynamicParams.categoryIds,
        subcategoryIds: dynamicParams.subcategoryIds,
        brandIds: dynamicParams.brandIds,
        segmentIds: dynamicParams.segmentIds
      };
      // Apply entity filters to the comparison parameters
      return applyEntityFilters(comparisonBaseParams);
    }

    // Basic comparison params without transformation
    const comparisonBaseParams = {
      ...baseParams,
      startWeekId: comparisonStartWeekId,
      endWeekId: comparisonEndWeekId
    };
    // Apply entity filters to the comparison parameters
    return applyEntityFilters(comparisonBaseParams, showMarketShare);
  }, [
    skipComparison,
    getComparisonParamsFromPrimary,
    primaryData,
    baseParams,
    comparisonStartWeekId,
    comparisonEndWeekId,
    applyEntityFilters,
    showMarketShare
  ]);

  // Use the useDorisData hook to fetch comparison data
  const { data: comparisonData, isLoading: isComparisonLoading } = useDorisData<TrafficSummaryResponse>({
    methodName,
    metricType,
    queryKey: `comparison_${queryKey}_${metricType}_${comparisonStartWeekId}_${comparisonEndWeekId}`,
    resultCount: resultCount || 1200,
    categoryIds: comparisonParams?.categoryIds,
    subcategoryIds: comparisonParams?.subcategoryIds,
    brandIds: comparisonParams?.brandIds,
    segmentIds: comparisonParams?.segmentIds,
    minRetailPrice: comparisonParams?.minRetailPrice,
    trendIds: comparisonParams?.trendIds,
    maxRetailPrice: comparisonParams?.maxRetailPrice,
    customTimePeriods: {
      startWeekId: comparisonStartWeekId,
      endWeekId: comparisonEndWeekId
    },
    additionalParams: showMarketShare ? marketShareNumeratorRequestBuilder(comparisonParams) : {},
    skipComparison: true,
    enabled: !skipComparison && (getComparisonParamsFromPrimary ? !!primaryData : true),
    queryOptions: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  });

  // Market data query for the comparison period
  const { data: marketComparisonData, isLoading: isMarketComparisonLoading } = useDorisData<TrafficSummaryResponse>({
    methodName,
    metricType,
    resultCount: resultCount || 1200,
    queryKey: `market_comparison_${queryKey}_${metricType}_${comparisonStartWeekId}_${comparisonEndWeekId}`,
    trendIds: comparisonParams?.trendIds,
    customTimePeriods: {
      startWeekId: comparisonStartWeekId,
      endWeekId: comparisonEndWeekId
    },
    skipComparison: true,
    additionalParams:
      showMarketShare && !skipComparison
        ? marketShareRequestBuilder(
            {
              ...baseParams,
              startWeekId: comparisonStartWeekId,
              endWeekId: comparisonEndWeekId
            },
            true
          ) || {}
        : {},
    enabled: showMarketShare && !skipComparison,
    queryOptions: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  });

  // Calculate market share values for both primary and comparison periods
  const marketShareValues = useMemo(() => {
    if (!showMarketShare) {
      return {
        primary: { marketShareValue: 0, clientValue: 0, marketValue: 0 },
        comparison: { marketShareValue: 0, clientValue: 0, marketValue: 0 }
      };
    }

    // For YTD comparison with prior year, we need to filter the data correctly
    const shouldFilterByCorrespondingWeeks =
      !isComparisonDataCustom && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR && weekRange === WeekRange.YTD;

    return {
      primary: calculateMarketShare(
        primaryData,
        marketPrimaryData,
        metricFieldToUse,
        marketShareField,
        shouldFilterByCorrespondingWeeks,
        weekIdsInRange,
        false
      ),
      comparison: skipComparison
        ? { marketShareValue: 0, clientValue: 0, marketValue: 0 }
        : calculateMarketShare(
            comparisonData,
            marketComparisonData,
            metricFieldToUse,
            marketShareField,
            shouldFilterByCorrespondingWeeks,
            weekIdsInRange,
            true
          )
    };
  }, [
    showMarketShare,
    primaryData,
    marketPrimaryData,
    metricFieldToUse,
    marketShareField,
    skipComparison,
    comparisonData,
    marketComparisonData,
    weekIdsInRange,
    isComparisonDataCustom,
    comparisonPeriod,
    weekRange
  ]);

  const getMetricTotal = useCallback(
    (response: TrafficSummaryResponse | undefined, isComparison = false) => {
      if (!response) {
        return 0;
      }

      // For YTD comparison with prior year, we need to filter the data correctly
      const shouldFilterByCorrespondingWeeks =
        isComparison &&
        !isComparisonDataCustom &&
        comparisonPeriod === ComparisonPeriod.PRIOR_YEAR &&
        weekRange === WeekRange.YTD;

      // Check if the response has records array (new API format)
      if (response.records && Array.isArray(response.records)) {
        // Get current year week ids for filtering if needed
        const currentYearWeekIds = shouldFilterByCorrespondingWeeks
          ? weekIdsInRange.map((weekId) => getPreviousYearWeekId(weekId))
          : null;

        // Filter and sum up all values from records
        return response.records
          .filter(
            (record) =>
              !shouldFilterByCorrespondingWeeks || (currentYearWeekIds && currentYearWeekIds.includes(record.weekId))
          )
          .reduce((sum, record) => sum + (record.value || 0), 0);
      }

      // Fallback to old format (direct property access)
      // For the old format, we can't filter by week, so we'll need to rely on the
      // backend to provide correctly filtered data
      return response[metricFieldToUse || metricType] || 0;
    },
    [metricFieldToUse, metricType, comparisonPeriod, weekRange, isComparisonDataCustom, weekIdsInRange]
  );

  // Calculate metrics for display
  const primaryTotal = showMarketShare ? marketShareValues.primary.marketShareValue : getMetricTotal(primaryData);

  const secondaryTotal = skipComparison
    ? 0
    : showMarketShare
    ? marketShareValues.comparison.marketShareValue
    : getMetricTotal(comparisonData, true);

  // Determine the metric type to use for formatting
  const metricTypeToUse = metricTypeDisplay || (showMarketShare ? METRICTYPE.PERCENT : METRICTYPE.VOLUME);

  // Calculate last data point values
  let lastPrimaryValue = 0;
  let lastSecondaryValue = 0;

  if (showMarketShare) {
    // When showing market share, we need to get the market share percentage values
    // Get the last primary value from the market share spline data or weekly share data
    if (marketShareValues.primary.weeklyMarketShare && marketShareValues.primary.weeklyMarketShare.length > 0) {
      const lastWeekRecord =
        marketShareValues.primary.weeklyMarketShare[marketShareValues.primary.weeklyMarketShare.length - 1];
      lastPrimaryValue = lastWeekRecord.value;
    } else if (marketShareValues.primary.marketShareValue !== 0) {
      lastPrimaryValue = marketShareValues.primary.marketShareValue;
    }

    // For secondary value, find the corresponding week's market share
    if (primaryData && primaryData.records && primaryData.records.length > 0) {
      const lastPrimaryWeekId = primaryData.records[primaryData.records.length - 1].weekId;

      // For prior year comparison, we need to adjust the weekId (e.g., 202511 -> 202411)
      const targetWeekId =
        comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
          ? lastPrimaryWeekId - 100 // Subtract 100 to go back a year in weekId format
          : lastPrimaryWeekId;

      // First try to find in weekly market share data
      if (marketShareValues.comparison.weeklyMarketShare) {
        const matchingRecord = marketShareValues.comparison.weeklyMarketShare.find(
          (record) => record.weekId === targetWeekId
        );

        if (matchingRecord) {
          lastSecondaryValue = matchingRecord.value;
        } else if (comparisonData && comparisonData.records && comparisonData.records.length > 0) {
          // Fallback to the spline data if we can't find in weekly data
          const matchingPoint = comparisonData.records.find((record) => record.weekId === targetWeekId);

          lastSecondaryValue = matchingPoint
            ? matchingPoint.value
            : comparisonData.records[comparisonData.records.length - 1].value;
        }
      }
    } else if (comparisonData && comparisonData.records && comparisonData.records.length > 0) {
      // Fallback to last value if no primary data
      lastSecondaryValue = comparisonData.records[comparisonData.records.length - 1].value;
    }
  } else {
    // Regular non-market share data - volumes
    // Check if we have records first for last value
    if (primaryData && primaryData.records && primaryData.records.length > 0) {
      // Use last value from records
      lastPrimaryValue = primaryData.records.sort((a, b) => b.weekId - a.weekId)[0].value || 0;
    } else {
      // Fallback to direct property access for old format
      lastPrimaryValue = primaryData ? primaryData[metricFieldToUse || metricType] || 0 : 0;
    }

    // Find the corresponding secondary value that matches the time position of the last primary value
    if (
      primaryData &&
      primaryData.records &&
      primaryData.records.length > 0 &&
      comparisonData &&
      comparisonData.records &&
      comparisonData.records.length > 0
    ) {
      // Get the last primary weekId from the response data directly

      const lastPrimaryWeekId = primaryData.records.sort((a, b) => b.weekId - a.weekId)[0].weekId;

      // For prior year comparison, we need to adjust the weekId (e.g., 202511 -> 202411)
      const targetWeekId =
        comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
          ? lastPrimaryWeekId - 100 // Subtract 100 to go back a year in weekId format
          : lastPrimaryWeekId;

      // Find the corresponding data point in the comparison response data directly
      const matchingRecord = comparisonData.records.find((record) => record.weekId === targetWeekId);
      if (matchingRecord) {
        lastSecondaryValue = matchingRecord.value;
      } else {
        // Fallback to searching in the transformed compData if no direct match found
        const matchingPoint = comparisonData.records.find((record) => record.weekId === targetWeekId);
        if (matchingPoint) {
          const { value } = matchingPoint;
          lastSecondaryValue = value;
        } else {
          const lastRecord = comparisonData.records[comparisonData.records.length - 1];
          lastSecondaryValue = lastRecord.value;
        }
      }
    } else if (comparisonData && comparisonData.records && comparisonData.records.length > 0) {
      // Fallback to the last value if no primary data
      const lastRecord = comparisonData.records[comparisonData.records.length - 1];
      lastSecondaryValue = lastRecord.value;
    }
  }

  // Calculate percent changes - only if we have comparison data
  const percentChange = skipComparison ? 0 : calculatePercentChange(primaryTotal, secondaryTotal);
  const percentChangeLastValues = skipComparison ? 0 : calculatePercentChange(lastPrimaryValue, lastSecondaryValue);

  // Format the data for use in charts and metrics displays
  return useMemo(() => {
    const isLoading =
      isPrimaryLoading ||
      (!skipComparison && isComparisonLoading) ||
      (showMarketShare && (isMarketPrimaryLoading || (!skipComparison && isMarketComparisonLoading)));

    // Convert raw API responses to time series data for charts
    const primData = primaryData ? getSplineDataFromResponse(primaryData, weekIdsInRange, metricFieldToUse, false) : [];

    const compData =
      !skipComparison && comparisonData
        ? getSplineDataFromResponse(comparisonData, comparisonWeekIdsInRange, metricFieldToUse, false).map((point) => [
            // If comparing with prior year but not custom, shift the timestamp to match current year
            !isComparisonDataCustom && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
              ? weekIdToTimestamp(getNextYearWeekId(timestampToWeekId(point[0])))
              : point[0],
            point[1]
          ])
        : [];

    const comparisonMarketShareSplineData =
      showMarketShare && !skipComparison && marketShareValues.comparison.weeklyMarketShare
        ? marketShareValues.comparison.weeklyMarketShare.map((record) => {
            const timestamp =
              !isComparisonDataCustom && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
                ? weekIdToTimestamp(getNextYearWeekId(record.weekId))
                : weekIdToTimestamp(record.weekId);
            return [timestamp, record.value]; // Convert to percentage for display
          })
        : [];

    // Create market share spline data if available
    const marketShareSplineData =
      showMarketShare && marketShareValues.primary.weeklyMarketShare
        ? [
            ...(comparisonPeriod === ComparisonPeriod.PRIOR_PERIOD && comparisonMarketShareSplineData.length > 0
              ? [comparisonMarketShareSplineData[comparisonMarketShareSplineData.length - 1]]
              : []),
            ...marketShareValues.primary.weeklyMarketShare.map((record) => [
              weekIdToTimestamp(record.weekId),
              record.value
            ])
          ]
        : [];

    // Collect additional metrics if requested
    const additionalMetrics = additionalMetricFields.reduce((acc, field) => {
      const primaryValue = primaryData ? primaryData[field] || 0 : 0;
      const comparisonValue = !skipComparison && comparisonData ? comparisonData[field] || 0 : 0;
      const fieldPercentChange = skipComparison ? 0 : calculatePercentChange(primaryValue, comparisonValue);

      acc[field] = {
        primaryValue,
        comparisonValue,
        percentChange: fieldPercentChange,
        formattedPrimaryValue: formatMetric(primaryValue, metricTypeToUse, {
          showFullValue: metricTypeToUse === METRICTYPE.PERCENT,
          decimalPlaces: 2,
          showNegative: true
        }),
        formattedComparisonValue: formatMetric(comparisonValue, metricTypeToUse, {
          showFullValue: metricTypeToUse === METRICTYPE.PERCENT,
          decimalPlaces: 2,
          showNegative: true
        }),
        formattedPercentChange: formatMetric(Math.abs(fieldPercentChange), METRICTYPE.PERCENT, {
          decimalPlaces: 2
        })
      };

      return acc;
    }, {} as Record<string, any>);

    const overlappedComparisonCountForSecondaryAverage = () => {
      if (skipComparison) {
        return 0;
      }

      const isYtdPriorYear = weekRange === WeekRange.YTD && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR;

      const comparisonWeeks = (comparisonData?.records?.map((r) => r.weekId) as number[]) ?? comparisonWeekIdsInRange;

      if (!isYtdPriorYear) {
        return comparisonWeeks.length;
      }

      const primaryWeeks = weekIdsInRange;

      const primaryPrevYearSet = new Set(primaryWeeks.map((w) => getPreviousYearWeekId(w)));

      let count = 0;
      for (const w of comparisonWeeks) {
        if (primaryPrevYearSet.has(w)) count++;
      }
      return count;
    };

    return {
      primaryData: [
        ...(!skipComparison &&
        !isComparisonDataCustom &&
        comparisonPeriod === ComparisonPeriod.PRIOR_PERIOD &&
        compData.length > 0
          ? [compData[compData.length - 1]]
          : []),
        ...primData
      ],
      secondaryData: compData,
      primarySlicedData: primData,
      secondarySlicedData: compData,
      // Include market share spline data for charts
      marketShareSplineData,
      comparisonMarketShareSplineData,
      mainRowsWithWeekId: primData,
      comparisonRowsWithWeekId: compData,
      lastPrimaryValue,
      lastSecondaryValue,
      primaryTotal,
      secondaryTotal,
      primaryAverageRate: safeDivide(primaryTotal, primData.length),
      secondaryAverageRate: safeDivide(secondaryTotal, overlappedComparisonCountForSecondaryAverage()),
      formattedPrimaryAverageRate: formatMetric(safeDivide(primaryTotal, primData.length), metricTypeToUse, {
        showFullValue: metricTypeToUse === METRICTYPE.PERCENT,
        decimalPlaces: 2,
        showNegative: true
      }),
      formattedSecondaryAverageRate: formatMetric(
        safeDivide(secondaryTotal, overlappedComparisonCountForSecondaryAverage()),
        metricTypeToUse,
        {
          showFullValue: metricTypeToUse === METRICTYPE.PERCENT,
          decimalPlaces: 2,
          showNegative: true
        }
      ),
      formattedPrimaryTotal: formatMetric(primaryTotal, metricTypeToUse, {
        showFullValue: metricTypeToUse === METRICTYPE.PERCENT,
        decimalPlaces: 2,
        showNegative: true
      }),
      formattedLastPrimaryValue: formatMetric(lastPrimaryValue, metricTypeToUse, {
        showFullValue: metricTypeToUse === METRICTYPE.PERCENT,
        decimalPlaces: 2,
        showNegative: true
      }),
      formattedLastSecondaryValue: formatMetric(lastSecondaryValue, metricTypeToUse, {
        showFullValue: metricTypeToUse === METRICTYPE.PERCENT,
        decimalPlaces: 2,
        showNegative: true
      }),
      percentChangeLastValues,
      formattedPercentChangeLastValues: formatMetric(Math.abs(percentChangeLastValues), METRICTYPE.PERCENT, {
        decimalPlaces: 2
      }),
      formattedSecondaryTotal: formatMetric(secondaryTotal, metricTypeToUse, {
        showFullValue: metricTypeToUse === METRICTYPE.PERCENT,
        decimalPlaces: 2,
        showNegative: true
      }),
      percentChange,
      formattedPercentChange: formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, {
        decimalPlaces: 2
      }),
      isLoading,
      response: { primary: primaryData, comparison: comparisonData },
      additionalMetrics,
      metricField: metricFieldToUse,
      marketShare: showMarketShare
        ? {
            primary: {
              ...marketShareValues.primary,
              formattedMarketShareValue: formatMetric(marketShareValues.primary.marketShareValue, metricTypeToUse, {
                decimalPlaces: 2,
                showFullValue: true
              }),
              formattedClientValue: formatMetric(marketShareValues.primary.clientValue, metricTypeToUse, {
                decimalPlaces: 0
              }),
              formattedMarketValue: formatMetric(marketShareValues.primary.marketValue, metricTypeToUse, {
                decimalPlaces: 0
              }),
              // Include weekly market share data for charting
              records: marketShareValues.primary.weeklyMarketShare || []
            },
            comparison: {
              ...marketShareValues.comparison,
              formattedMarketShareValue: formatMetric(marketShareValues.comparison.marketShareValue, metricTypeToUse, {
                decimalPlaces: 2,
                showFullValue: true
              }),
              formattedClientValue: formatMetric(marketShareValues.comparison.clientValue, metricTypeToUse, {
                decimalPlaces: 0
              }),
              formattedMarketValue: formatMetric(marketShareValues.comparison.marketValue, metricTypeToUse, {
                decimalPlaces: 0
              }),
              // Include weekly market share data for charting
              records: marketShareValues.comparison.weeklyMarketShare || []
            }
          }
        : undefined
    };
  }, [
    isPrimaryLoading,
    skipComparison,
    isComparisonLoading,
    showMarketShare,
    isMarketPrimaryLoading,
    isMarketComparisonLoading,
    primaryData,
    weekIdsInRange,
    metricFieldToUse,
    comparisonData,
    comparisonWeekIdsInRange,
    marketShareValues.comparison,
    marketShareValues.primary,
    comparisonPeriod,
    additionalMetricFields,
    isComparisonDataCustom,
    lastPrimaryValue,
    lastSecondaryValue,
    primaryTotal,
    secondaryTotal,
    formatMetric,
    metricTypeToUse,
    percentChangeLastValues,
    percentChange,
    weekRange
  ]);
};

export default useDorisMetricByWeekId;
